import React, { useEffect, useRef, useState } from 'react';
import { formatINR } from '@/utils/format';
import { getOrderCostBreakdown, type OrderCostBreakdown } from '@/services/costingService';

export interface ActualCostModalData {
  id?: string;
  productionOrderId?: string;
  poNumber?: string;
  productionOrderNumber?: string;
  po_number?: string;
  productName?: string;
  item_name?: string;
  variantName?: string;
  item_code?: string;
  qty?: number;
  producedQty?: number;
  planned_qty?: number;
  unit?: string;
  status?: string;
  stdMaterial?: number;
  actMaterial?: number;
  materialCost?: number;
  matVar?: number;
  stdLabour?: number;
  actLabour?: number;
  labourCost?: number;
  labVar?: number;
  stdOverhead?: number;
  actOverhead?: number;
  overheadCost?: number;
  ohVar?: number;
  stdTotal?: number;
  actTotal?: number;
  totalCost?: number;
  totalVar?: number;
  salePrice?: number;
  sale_price?: number;
}

interface ActualCostDetailsModalProps {
  open: boolean;
  onClose: () => void;
  row?: ActualCostModalData | null;
  costRecord?: ActualCostModalData | null;
  order?: ActualCostModalData | null;
}

export const ActualCostDetailsModal: React.FC<ActualCostDetailsModalProps> = ({
  open,
  onClose,
  row,
  costRecord,
  order,
}) => {
  const printRef = useRef<HTMLDivElement>(null);
  const [breakdown, setBreakdown] = useState<OrderCostBreakdown | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const data = row || costRecord || order;
  // Costing rows carry the production order id; the order detail page passes the order itself
  const orderRef = data
    ? String(data.productionOrderId || (order ? data.id : '') || data.poNumber || data.productionOrderNumber || data.po_number || '')
    : '';

  // Live standard vs actual breakdown of the order
  useEffect(() => {
    if (!open || !orderRef) {
      setBreakdown(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    getOrderCostBreakdown(orderRef)
      .then((res) => {
        if (cancelled) return;
        if (res?.success && res.data) setBreakdown(res.data);
        else {
          setBreakdown(null);
          setLoadError(res?.message || 'Cost breakdown is not available for this order');
        }
      })
      .catch((err: any) => {
        if (cancelled) return;
        setBreakdown(null);
        setLoadError(err?.message || 'Failed to load cost breakdown');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, orderRef]);

  if (!open || !data) return null;

  const poNumber = breakdown?.poNumber || data.poNumber || data.productionOrderNumber || data.po_number || '';
  const productName = breakdown?.productName || data.productName || data.item_name || '';
  const variantName = data.variantName || breakdown?.productCode || data.item_code || '';
  const qty = breakdown
    ? (breakdown.producedQty > 0 ? breakdown.producedQty : breakdown.plannedQty)
    : Number(data.qty || data.producedQty || data.planned_qty || 0);
  const unit = breakdown?.unit || data.unit || '';
  const status = breakdown?.status || data.status || '';

  // Sale price & revenue
  const unitSalePrice = breakdown ? breakdown.salePrice : Number(data.salePrice ?? data.sale_price ?? 0);
  const totalRevenue = unitSalePrice * qty;

  // Summary rows
  const stdMat = breakdown ? breakdown.standard.material : Number(data.stdMaterial || 0);
  const actMat = breakdown ? breakdown.actual.material : Number(data.actMaterial ?? data.materialCost ?? 0);
  const matVar = stdMat - actMat;

  const stdLab = breakdown ? breakdown.standard.labour : Number(data.stdLabour || 0);
  const actLab = breakdown ? breakdown.actual.labour : Number(data.actLabour ?? data.labourCost ?? 0);
  const labVar = stdLab - actLab;

  const stdOh = breakdown ? breakdown.standard.overhead : Number(data.stdOverhead || 0);
  const actOh = breakdown ? breakdown.actual.overhead : Number(data.actOverhead ?? data.overheadCost ?? 0);
  const ohVar = stdOh - actOh;

  const actualCost = breakdown ? breakdown.actual.total : Number(data.actTotal ?? data.totalCost ?? 0);
  const stdCost = breakdown ? breakdown.standard.total : Number(data.stdTotal || 0);
  const totalVarVal = stdCost - actualCost;

  // Actual Gross Margin
  const actualGrossMargin = totalRevenue - actualCost;
  const actualMarginPct = totalRevenue > 0 ? (actualGrossMargin / totalRevenue) * 100 : 0;

  // Standard margin comparison
  const stdGrossMargin = totalRevenue - stdCost;
  const stdMarginPct = totalRevenue > 0 ? (stdGrossMargin / totalRevenue) * 100 : 0;
  const marginDiffPct = actualMarginPct - stdMarginPct;

  // Grade: by margin when the item has a sale price, otherwise by cost variance
  const marginGrade = actualMarginPct >= 50 ? 'Grade A Excellent' : actualMarginPct >= 20 ? 'Grade B Good' : 'Grade C Average';
  const gradeText = totalRevenue > 0 ? marginGrade : breakdown?.grade || '—';
  const gradeColor = /Grade A/.test(gradeText)
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : /Grade B/.test(gradeText)
      ? 'bg-blue-50 text-blue-700 border-blue-200'
      : 'bg-amber-50 text-amber-700 border-amber-200';

  // Cost elements: one line per BOM material, per work order stage, plus overhead
  const typeCode: Record<string, string> = { MATERIAL: 'MAT', LABOUR: 'LAB', OVERHEAD: 'OVH' };
  const elements: { element: string; type: string; stdCost: number; actCost: number; varVal: number; isRejection?: boolean }[] =
    (breakdown?.elements || []).map((e) => ({
      element: e.description,
      type: typeCode[e.type] || e.type,
      stdCost: e.stdAmount,
      actCost: e.actAmount,
      varVal: e.stdAmount - e.actAmount,
    }));
  if (breakdown && breakdown.rejectionShare > 0) {
    elements.push({
      element: `Rejected units cost (${breakdown.rejectedQty} ${unit}) — included in the lines above`,
      type: 'REJECTION',
      stdCost: 0,
      actCost: breakdown.rejectionShare,
      varVal: 0,
      isRejection: true,
    });
  }

  const handlePrint = () => {
    window.print();
  };

  const renderVariancePill = (val: number) => {
    if (val < 0) {
      return <span className="text-rose-600 font-semibold">↑ {formatINR(Math.abs(val))}</span>;
    }
    if (val > 0) {
      return <span className="text-emerald-600 font-semibold">↓ {formatINR(val)}</span>;
    }
    return <span className="text-slate-500 font-semibold">= ₹0</span>;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/50 backdrop-blur-xs overflow-y-auto">
      <div
        ref={printRef}
        className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-xl my-8 overflow-hidden animate-in fade-in zoom-in-95 duration-150"
      >
        {/* Header */}
        <div className="px-6 py-4.5 border-b border-slate-100 flex items-center justify-between bg-white sticky top-0 z-10">
          <h2 className="text-base font-bold text-slate-900 tracking-tight">
            Actual Cost Details — {poNumber}
          </h2>
          <button
            onClick={onClose}
            className="w-7 h-7 inline-flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="p-6 space-y-6 max-h-[80vh] overflow-y-auto">
          {/* 1. ORDER INFO */}
          <div>
            <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">
              Order Info
            </div>
            <div className="bg-[#f8fafc] border border-slate-200/80 rounded-xl p-4 grid grid-cols-2 gap-4 text-xs">
              <div>
                <span className="text-slate-400 block mb-0.5">PO Number</span>
                <span className="font-bold text-slate-900 text-sm">{poNumber}</span>
              </div>
              <div>
                <span className="text-slate-400 block mb-0.5">Product</span>
                <span className="font-bold text-slate-900 block">{productName}</span>
                {variantName && (
                  <span className="text-slate-500 text-[11px] block">{variantName}</span>
                )}
              </div>
              <div>
                <span className="text-slate-400 block mb-0.5">Quantity</span>
                <span className="font-bold text-slate-900">{qty} {unit}</span>
              </div>
              <div>
                <span className="text-slate-400 block mb-0.5">Status</span>
                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold tracking-wider bg-emerald-100/70 text-emerald-700">
                  {status}
                </span>
              </div>
            </div>
          </div>

          {/* 2. ACTUAL COST ELEMENT BREAKDOWN — VS STANDARD */}
          <div>
            <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">
              Actual Cost Element Breakdown — vs Standard
            </div>
            <div className="border border-slate-200/80 rounded-xl overflow-hidden">
              <table className="w-full text-xs text-left">
                <thead>
                  <tr className="bg-slate-50/70 border-b border-slate-200 text-slate-500 text-[11px] font-semibold">
                    <th className="py-2.5 px-3">Element</th>
                    <th className="py-2.5 px-2 text-center">Type</th>
                    <th className="py-2.5 px-3 text-right">Std Cost</th>
                    <th className="py-2.5 px-3 text-right">Act Cost</th>
                    <th className="py-2.5 px-3 text-right">Variance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-medium">
                  {elements.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-4 px-3 text-center text-slate-400">
                        {loading ? 'Loading cost breakdown…' : loadError || 'No cost elements recorded for this order'}
                      </td>
                    </tr>
                  )}
                  {elements.map((item, idx) => (
                    <tr key={idx} className="hover:bg-slate-50/50">
                      <td className="py-2.5 px-3 text-slate-900 font-medium">{item.element}</td>
                      <td className="py-2.5 px-2 text-center">
                        <span
                          className={`px-1.5 py-0.5 text-[10px] font-bold rounded tracking-wide ${
                            item.type === 'MAT'
                              ? 'bg-blue-50 text-blue-700'
                              : item.type === 'LAB'
                              ? 'bg-emerald-50 text-emerald-700'
                              : item.type === 'OVH'
                              ? 'bg-purple-50 text-purple-700'
                              : 'bg-rose-50 text-rose-700'
                          }`}
                        >
                          {item.type}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-right text-slate-600">
                        {item.stdCost > 0 ? formatINR(item.stdCost) : '—'}
                      </td>
                      <td className="py-2.5 px-3 text-right text-slate-900 font-semibold">
                        {formatINR(item.actCost)}
                      </td>
                      <td className="py-2.5 px-3 text-right">
                        {renderVariancePill(item.varVal)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* 3. ACTUAL VS STANDARD SUMMARY */}
          <div>
            <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">
              Actual vs Standard Summary
            </div>
            <div className="border border-slate-200/80 rounded-xl overflow-hidden">
              <table className="w-full text-xs text-left">
                <thead>
                  <tr className="bg-slate-50/70 border-b border-slate-200 text-slate-500 text-[11px] font-semibold">
                    <th className="py-2.5 px-3"></th>
                    <th className="py-2.5 px-3 text-right">Standard</th>
                    <th className="py-2.5 px-3 text-right">Actual</th>
                    <th className="py-2.5 px-3 text-right">Variance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-medium">
                  <tr>
                    <td className="py-2.5 px-3 text-slate-900 font-bold">Material</td>
                    <td className="py-2.5 px-3 text-right text-slate-700">{formatINR(stdMat)}</td>
                    <td className="py-2.5 px-3 text-right text-slate-900 font-bold">{formatINR(actMat)}</td>
                    <td className="py-2.5 px-3 text-right">{renderVariancePill(matVar)}</td>
                  </tr>
                  <tr>
                    <td className="py-2.5 px-3 text-slate-900 font-bold">Labour</td>
                    <td className="py-2.5 px-3 text-right text-slate-700">{formatINR(stdLab)}</td>
                    <td className="py-2.5 px-3 text-right text-slate-900 font-bold">{formatINR(actLab)}</td>
                    <td className="py-2.5 px-3 text-right">{renderVariancePill(labVar)}</td>
                  </tr>
                  <tr>
                    <td className="py-2.5 px-3 text-slate-900 font-bold">Overhead</td>
                    <td className="py-2.5 px-3 text-right text-slate-700">{formatINR(stdOh)}</td>
                    <td className="py-2.5 px-3 text-right text-slate-900 font-bold">{formatINR(actOh)}</td>
                    <td className="py-2.5 px-3 text-right">{renderVariancePill(ohVar)}</td>
                  </tr>
                  <tr className="bg-slate-50/40">
                    <td className="py-2.5 px-3 text-slate-900 font-extrabold uppercase">TOTAL</td>
                    <td className="py-2.5 px-3 text-right text-slate-900 font-bold">{formatINR(stdCost)}</td>
                    <td className="py-2.5 px-3 text-right text-slate-900 font-bold">{formatINR(actualCost)}</td>
                    <td className="py-2.5 px-3 text-right">{renderVariancePill(totalVarVal)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* 4. PROFITABILITY AT ACTUAL COST */}
          <div>
            <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">
              Profitability at Actual Cost
            </div>
            <div className="bg-[#f8fafc] border border-slate-200/80 rounded-xl p-4 space-y-2 text-xs">
              <div className="flex justify-between items-center text-slate-600">
                <span>Sale Price</span>
                <span className="font-bold text-slate-900">{formatINR(unitSalePrice)}</span>
              </div>
              <div className="flex justify-between items-center text-slate-600">
                <span>Total Revenue ({qty} units)</span>
                <span className="font-bold text-slate-900">{formatINR(totalRevenue)}</span>
              </div>
              <div className="flex justify-between items-center text-slate-600">
                <span>Actual Cost</span>
                <span className="font-bold text-slate-900">{formatINR(actualCost)}</span>
              </div>
              <div className="pt-2 border-t border-slate-200 flex justify-between items-center">
                <span className="font-bold text-slate-900">Actual Gross Margin</span>
                <span className="font-bold text-slate-900 text-sm">
                  {formatINR(actualGrossMargin)} ({actualMarginPct.toFixed(1)}%)
                </span>
              </div>
              <div className="flex justify-between items-center text-[11px] text-slate-500">
                <span>vs Standard Margin ({stdMarginPct.toFixed(1)}%)</span>
                <span className={marginDiffPct < 0 ? 'text-rose-600 font-semibold' : 'text-emerald-600 font-semibold'}>
                  {marginDiffPct < 0 ? '↓' : '↑'} {Math.abs(marginDiffPct).toFixed(1)}%
                </span>
              </div>
              <div className="pt-2 flex justify-end">
                <span className={`px-2.5 py-1 text-[11px] font-bold rounded-full border ${gradeColor}`}>
                  {gradeText}
                </span>
              </div>
            </div>
          </div>

          {/* 5. Print Button */}
          <button
            onClick={handlePrint}
            className="w-full py-2.5 px-4 bg-[#0f172a] hover:bg-[#1e293b] text-white font-semibold text-xs rounded-xl flex items-center justify-center gap-2 shadow-sm transition-colors cursor-pointer"
          >
            <i className="ri-printer-line text-sm" /> Print Cost Sheet
          </button>
        </div>
      </div>
    </div>
  );
};
